import {DI_TOKENS} from '@/di/di.tokens';
import {ShareState} from '@/stores/ShareStore/ShareStore.types';

import {injected} from 'brandi';

import {ResourcesAll} from '@/resources';
import {ShareService} from '@/services/ShareService/ShareService';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {AnalyticsConditionsType} from '@/services/TrackingService/constants';
import {ShareStore} from '@/stores/ShareStore/ShareStore';
import {GlobalWindow} from '@/types';

export class ShareUseCase {
  private readonly timeout = 3000;
  private hideTimeoutConditions = 0;
  private hideTimeoutProjectRef = 0;

  constructor(
    private readonly resources: ResourcesAll,
    private readonly globalWindow: GlobalWindow,
    private readonly shareStore: ShareStore,
    private readonly shareService: ShareService,
    private readonly trackingService: TrackingService,
  ) {}

  public startProjectShare = (): void => {
    if (this.canShare()) {
      void this.shareProjectNative();
    } else {
      this.copyProjectLink();
    }
  };

  public copyConditionsLinkToClipboard = (link: string): void => {
    if (this.shareStore.isConditionsLinkShareStatusSuccess()) return;
    this.trackingService.trackClickOnConditions(AnalyticsConditionsType.CopyMb);

    this.globalWindow.clearTimeout(this.hideTimeoutConditions);

    const newShareState = this.shareService.copyToClipboard(link);

    this.shareStore.setConditionsLinkShareStatus(newShareState);

    this.hideTimeoutConditions = this.globalWindow.setTimeout(() => {
      this.shareStore.resetConditionsLinkShareStatus();
    }, this.timeout);
  };

  public copyPromocodeToClipboard = (data: string): ShareState => {
    return this.shareService.copyToClipboard(data);
  };

  public canShare = (): boolean => {
    return this.shareService.canShare({url: this.resources.content.shareButton.shareLink});
  };

  private shareProjectNative = async (): Promise<void> => {
    const shareUrl = this.getShareUrl();
    const newShareState = await this.shareNative(shareUrl);
    this.shareStore.setProjectRefShareStatus(newShareState);

    this.hideTimeoutProjectRef = this.globalWindow.setTimeout(() => {
      this.shareStore.resetProjectRefShareStatus();
    }, this.timeout);
  };

  private copyProjectLink = (): void => {
    if (this.shareStore.isProjectRefShareStatusSuccess()) {
      return;
    }
    const shareUrl = this.getShareUrl();
    this.globalWindow.clearTimeout(this.hideTimeoutProjectRef);
    const newShareState = this.shareService.copyToClipboard(shareUrl);
    this.shareStore.setProjectRefShareStatus(newShareState);

    this.hideTimeoutProjectRef = this.globalWindow.setTimeout(() => {
      this.shareStore.resetProjectRefShareStatus();
    }, this.timeout);
  };

  private shareNative = (shareUrl: string): Promise<ShareState> => {
    return this.shareService.share({url: shareUrl});
  };

  private getShareUrl = (): string => {
    return this.resources.content.shareButton.shareLink;
  };
}

injected(
  ShareUseCase,
  DI_TOKENS.resources,
  DI_TOKENS.globalWindow,
  DI_TOKENS.shareStore,
  DI_TOKENS.shareService,
  DI_TOKENS.trackingService,
);
